'use client';import { JSONWebToken } from '@parkingbase/ecommerce-api-demo';


import { useEffect } from 'react';
import { useAuthStore } from '@/stores/user.store';
import { readImpersonationSession } from '@/lib/impersonation';


/**
 * Resolves the session for this document: the SSR refresh-token exchange for a
 * normal customer, or the per-tab impersonation session for a back-office
 * support popup.
 *
 * ## Invariants:
 * - Impersonation is adopted here, not only at /sso: this provider is an
 *   ancestor of the page, so its effect runs after the page's and would
 *   otherwise reset what /sso just wrote.
 * - It is checked BEFORE the SSR session. The support user may also be logged in
 *   as themselves, and that cookie is origin-wide, so `tokenDetails` is often
 *   *their* session while the frame should show the customer's. Safe to check
 *   first: readImpersonationSession() is null outside a back-office frame.
 * - An impersonated session never populates `refreshToken`, so `initialize()`
 *   can't refresh it onto the cookie (PB-3817).
 */
export function HydrateStoreProvider({
  children,
  tokenDetails,
}: {
  children: React.ReactNode;
  tokenDetails: JSONWebToken | null;
}) {
  useEffect(() => {
    const impersonated = readImpersonationSession();
    if (impersonated) {
      useAuthStore.setState({
        tokenDetails: impersonated,
        isAuthenticated: true,
        isAuthInitialized: true,
        isImpersonating: true,
        token: `${impersonated.token_type} ${impersonated.access_token}`,
        refreshToken: null,
        isLoadingUser: true,
      });
      return;
    }

    if (tokenDetails) {
      const composedToken = `${tokenDetails.token_type} ${tokenDetails.access_token}`;
      useAuthStore.setState({
        tokenDetails,
        isAuthenticated: true,
        isAuthInitialized: true,
        isImpersonating: false,
        token: composedToken,
        refreshToken: tokenDetails.refresh_token,
        isLoadingUser: true,
      });
      return;
    }

    useAuthStore.setState({
      tokenDetails: null,
      isAuthenticated: false,
      isAuthInitialized: true,
      isImpersonating: false,
      token: null,
      refreshToken: null,
    });
  }, [tokenDetails]);

  return <>{children}</>;
}
